<script setup lang="ts">
import FlatRouterLinkButton from '@/components/atoms/FlatRouterLinkButton.vue'
import BreadcrumbList from '@/components/molecules/BreadcrumbList.vue'
import EnqueteIndexPagePagination from './EnqueteIndexPagePagination.vue'
import { computed, ref } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import {
  useEnqueteIndexPageQuery,
  type EnqueteIndexPageQueryVariables,
} from './EnqueteIndexPage.generated'

const route = useRoute('EnqueteIndexPage')

const currentPageNumber = ref(1)

const queryInput = computed<EnqueteIndexPageQueryVariables>(() => {
  const limit = 25

  return {
    input: {
      limit,
      offset: (currentPageNumber.value - 1) * limit,
    },
  }
})

const pageQuery = useEnqueteIndexPageQuery(queryInput, {
  fetchPolicy: 'no-cache',
})

const movePage = (page: number) => {
  currentPageNumber.value = page
}
</script>

<template>
  <div :class="$style.wrapper">
    <BreadcrumbList :links="route.meta.breadcrumbs" />
    <section :class="$style.container">
      <header :class="$style.header">
        <h1>アンケート</h1>
        <FlatRouterLinkButton
          variant="primary"
          size="medium"
          :to="{ name: 'EnqueteNewPage' }"
        >
          新規作成
        </FlatRouterLinkButton>
      </header>
      <hr :class="$style.hr" />
      <div :class="$style.containerInner" v-if="pageQuery.result.value">
        <div :class="$style.paginationWrapper">
          <EnqueteIndexPagePagination
            :paginationInfo="pageQuery.result.value.enquetes.paginationInfo"
            @move="movePage"
          />
        </div>
        <table :class="$style.table">
          <colgroup>
            <col />
          </colgroup>
          <thead>
            <tr>
              <th>タイトル</th>
            </tr>
          </thead>
          <tbody v-if="pageQuery.result.value.enquetes.enquetes.length > 0">
            <tr
              v-for="enquete of pageQuery.result.value.enquetes.enquetes"
              :key="enquete.guid"
            >
              <td>
                <RouterLink
                  :to="{
                    name: 'EnqueteEditPage',
                    params: {
                      entranceGUID: route.params.entranceGUID,
                      enqueteGUID: enquete.guid,
                    },
                  }"
                  >{{ enquete.title }}</RouterLink
                >
              </td>
            </tr>
          </tbody>
          <tbody
            v-else-if="pageQuery.result.value?.enquetes.enquetes.length === 0"
          >
            <tr>
              <td colspan="6" :class="$style.none">アンケートがありません</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<style module lang="scss">
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 auto;
  width: 860px;
}

.container {
  background-color: $color-surface-primary;
  padding: 16px;
  border-radius: 10px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.paginationWrapper {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.hr {
  border: none;
  border-top: 1px solid $color-border-low-emphasis;
  margin: 16px 0;
}

.containerInner {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.table {
  border-collapse: collapse;

  thead {
    background-color: $color-background-base;

    th {
      padding: 12px;
      text-align: left;
    }
  }

  tbody {
    tr {
      &:not(:first-child) {
        border-top: 1px solid $color-border-low-emphasis;
      }

      td {
        padding: 24px 12px;

        a {
          display: block;
          color: inherit;
          text-decoration: underline;
        }
      }
    }
  }
}

.none {
  text-align: center;
}
</style>
